import { useMemo } from "react";

import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { SeriesAreaOptions } from "highcharts";
import { renderToString } from "react-dom/server";
import styled from "styled-components";

import { PieChart, Show, DollarSign } from "@similarweb/icons";
import type {
    ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint,
    ProWebsiteControllersApiWidgetAdIntelligencePerformanceOverTimeResponse,
} from "@similarweb/pro-api-client";
import { serverResponseFormat, type SimplifiedSeries } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { ColumnDefinitionWithoutTitle } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter, percentageSignOnlyFilter, spendFilter } from "app/filters/ngFilters";
import type { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
import { GRANULARITY_SWITCHER_OPTIONS } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import { tooltipPositioner } from "app/services/HighchartsPositioner";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getTooltipHeaderElement } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";

import { EDataType } from "../../advertisers/overview/components/types";
import { getXAxisTickPositioner, xAxisFormatter } from "../../common/overview/utils/graphUtils";
import { NA_VALUE } from "../../common/utils/utils";

import { DISPLAY_MODES } from "./types";
import type { ChannelKey, GranularityKey, DisplayMode } from "./types";

export const metricTabs = [
    {
        type: EDataType.SPEND,
        Icon: DollarSign,
        labelKey: "advertiser.activity.overview.performance.over.time.widget.spend",
    },
    {
        type: EDataType.IMPRESSIONS,
        Icon: Show,
        labelKey: "advertiser.activity.overview.performance.over.time.widget.impressions",
    },
    {
        type: EDataType.VISITS,
        Icon: PieChart,
        labelKey: "advertiser.activity.overview.performance.over.time.widget.visits",
    },
] as const;

const channelConfig: Record<
    ChannelKey,
    {
        id: ChannelKey;
        translationKey: string;
        getColor: (granularity?: GranularitySwitcherEnum) => string;
    }
> = {
    display: {
        id: "display",
        translationKey: "advertiser.activity.overview.performance.over.time.widget.channel.display",
        getColor: () => colorsPalettes.blue[400],
    },
    search: {
        id: "search",
        translationKey: "advertiser.activity.overview.performance.over.time.widget.channel.search",
        getColor: () => colorsPalettes.midnight[500],
    },
};

export const displayModeOptions = [
    {
        id: DISPLAY_MODES.values,
        title: "#",
        value: DISPLAY_MODES.values,
    },
    {
        id: DISPLAY_MODES.percentage,
        title: "%",
        value: DISPLAY_MODES.percentage,
    },
];

export const TabsContainer = styled(Flex)`
    margin-bottom: 20px;
    gap: 16px;
    position: relative;

    &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: ${colorsPalettes.carbon[50]};
    }
`;

const TooltipTitle = styled.span`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

interface ITooltipRow {
    label: {
        value: string;
        color: string;
    };
    count: {
        value: string;
    };
}
const tooltipColumnDefinition: ColumnDefinitionWithoutTitle<ITooltipRow>[] = [
    {
        key: "0",
        selector: "label" as const,
        align: "left",
        size: "172px",
        render: ({ label }) => (
            <ChartTooltip.LabelCell
                label={label.value}
                backgroundColor={label.color}
            />
        ),
    },
    {
        key: "1",
        selector: "count" as const,
        align: "right",
        size: "50px",
        render: ({ count }) => <ChartTooltip.ValueCell value={count.value} />,
    },
];

export const formatLegendValue = (value: number, metricType: EDataType, displayMode: DisplayMode): string => {
    if (!value) {
        return NA_VALUE;
    }

    if (displayMode === DISPLAY_MODES.percentage) {
        return percentageSignFilter()(value, 1) as string;
    }

    if (metricType === EDataType.SPEND) {
        return spendFilter(value, abbrNumberFilter());
    }
    return abbrNumberFilter()(value) as string;
};

export const hasNoPerformanceData = (
    ...channelDataArrays: Array<Array<[string, ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint]>>
): boolean => {
    return channelDataArrays.every((channelData) =>
        channelData.every(([, item]) => !item.spend && !item.impressions && !item.visits),
    );
};

export const hasNoDataInSelectedMetric = (graphData: SimplifiedSeries<SeriesAreaOptions>): boolean => {
    if (!graphData || graphData.length === 0) {
        return true;
    }

    return graphData.every((series) =>
        series.data.every((dataPoint) => {
            if (Array.isArray(dataPoint)) {
                const value = dataPoint[1];
                return !value;
            }
            return !dataPoint;
        }),
    );
};

type SelectedBreakdownData = Partial<
    Record<ChannelKey, Array<[string, ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint]>>
>;
type MetricKey = "spend" | "impressions" | "visits";
type PerformanceTotals = Partial<
    Record<ChannelKey, Pick<ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint, MetricKey>>
>;
type SeriesMap = Record<ChannelKey, SimplifiedSeries<SeriesAreaOptions>[number]>;
type ChannelSums = Record<ChannelKey, number>;

const getGranularityKey = (selectedGranularity: GranularitySwitcherEnum): GranularityKey => {
    return GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value.toLowerCase() as GranularityKey;
};

const getMetricKey = (selectedMetric: EDataType): MetricKey => {
    return selectedMetric.toLowerCase() as MetricKey;
};

export const useGraphData = (
    data: ProWebsiteControllersApiWidgetAdIntelligencePerformanceOverTimeResponse | undefined,
    selectedGranularity: GranularitySwitcherEnum,
    selectedMetric: EDataType,
    displayMode: DisplayMode,
) => {
    const selectedBreakdownData = useMemo<SelectedBreakdownData>(() => {
        if (!data) {
            return {};
        }

        const currentGranularityKey = getGranularityKey(selectedGranularity);

        return Object.entries(data).reduce(
            (acc, [channel, channelData]) => {
                if (channelData && typeof channelData === "object") {
                    const typedChannelData =
                        channelData as ProWebsiteControllersApiWidgetAdIntelligencePerformanceOverTimeResponse[ChannelKey];
                    const granularityData = typedChannelData[currentGranularityKey]?.data;
                    if (granularityData && typeof granularityData === "object") {
                        acc[channel as ChannelKey] = Object.entries(granularityData);
                    }
                }
                return acc;
            },
            {} as Record<ChannelKey, Array<[string, ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint]>>,
        );
    }, [data, selectedGranularity]);

    const performanceTotals = useMemo<PerformanceTotals>(() => {
        if (!data) {
            return {};
        }

        const currentGranularityKey = getGranularityKey(selectedGranularity);
        const result: PerformanceTotals = {};

        for (const [channel, channelData] of Object.entries(data)) {
            if (channelData && typeof channelData === "object" && channel in channelConfig) {
                const averages = channelData[currentGranularityKey]?.averages;
                if (averages) {
                    result[channel as ChannelKey] = {
                        spend: averages.averageSpend,
                        impressions: averages.averageImpressions,
                        visits: averages.averageVisits,
                    };
                }
            }
        }

        return result;
    }, [data, selectedGranularity]);

    const graphData: SimplifiedSeries<SeriesAreaOptions> = useMemo(() => {
        const metricKey = getMetricKey(selectedMetric);

        const seriesMap: SeriesMap = {} as SeriesMap;
        for (const [channel, config] of Object.entries(channelConfig)) {
            seriesMap[channel as ChannelKey] = {
                id: config.id,
                name: translate(config.translationKey),
                data: [],
                color: config.getColor(selectedGranularity),
                total: 0,
                visible: true,
            };
        }

        const dataMap = new Map<string, ChannelSums>();
        const channelSums: ChannelSums = {} as ChannelSums;
        for (const channel of Object.keys(channelConfig)) {
            channelSums[channel as ChannelKey] = 0;
        }

        for (const [channel, channelData] of Object.entries(selectedBreakdownData)) {
            if (channelData) {
                for (const [dateString, performanceItem] of channelData) {
                    const value = performanceItem[metricKey] || 0;
                    const existing = dataMap.get(dateString) || { ...channelSums };
                    dataMap.set(dateString, { ...existing, [channel as ChannelKey]: value });
                }
            }
        }

        dataMap.forEach((values, dateString) => {
            const timestampValue = dayjs.utc(dateString, serverResponseFormat).valueOf();

            const channelKeys = Object.keys(values);
            const totalValue = channelKeys.reduce((sum, channel) => sum + values[channel], 0);

            channelKeys.forEach((channel) => {
                channelSums[channel] += values[channel];

                if (displayMode === DISPLAY_MODES.percentage) {
                    const percentage = totalValue > 0 ? (values[channel] / totalValue) * 100 : 0;
                    seriesMap[channel].data.push([timestampValue, percentage]);
                } else {
                    seriesMap[channel].data.push([timestampValue, values[channel]]);
                }
            });
        });

        Object.values(seriesMap).forEach((series) => {
            series.data.sort((a, b) => a[0] - b[0]);
        });

        if (displayMode === DISPLAY_MODES.percentage) {
            const channelKeys = Object.keys(channelSums);
            const totalSum = channelKeys.reduce((sum, channel) => sum + channelSums[channel], 0);

            if (totalSum > 0) {
                channelKeys.forEach((channel) => {
                    seriesMap[channel].total = channelSums[channel] / totalSum;
                });
            } else {
                Object.values(seriesMap).forEach((series) => {
                    series.total = 0;
                });
            }
        } else {
            const channelKeys = Object.keys(seriesMap);
            channelKeys.forEach((channel) => {
                seriesMap[channel].total = performanceTotals?.[channel]?.[metricKey] || 0;
            });
        }

        return Object.values(seriesMap).reverse();
    }, [performanceTotals, selectedBreakdownData, selectedMetric, displayMode, selectedGranularity]);

    return { graphData, selectedBreakdownData };
};

const MAX_TICKS = 20;

export const getChartConfig = (
    selectedMetric: EDataType,
    displayMode: DisplayMode,
    selectedGranularity: GranularitySwitcherEnum,
    selectedBreakdownData: SelectedBreakdownData,
    to: Dayjs,
): Highcharts.Options => {
    const granularityValue = GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value as GranularityNames;
    const isMonthly = granularityValue === GranularityNames.MONTHLY;

    const allUniqueDates = new Set(
        Object.values(selectedBreakdownData).flatMap((channelData) => channelData.map(([date]) => date)),
    );
    const uniqueDateCount = allUniqueDates.size;
    const expectedTickCount = Math.min(uniqueDateCount, MAX_TICKS);

    const totalDataPoints = Object.values(selectedBreakdownData).reduce(
        (sum, channelData) => sum + channelData.length,
        0,
    );
    const hasSingleDataPoint = totalDataPoints <= Object.keys(selectedBreakdownData).length;

    const formatValue = (value: number) => {
        if (displayMode === DISPLAY_MODES.percentage) {
            return percentageSignOnlyFilter()(value, 1);
        }
        if (selectedMetric === EDataType.SPEND) {
            return spendFilter(value, abbrNumberFilter());
        }
        return abbrNumberFilter()(value) as string;
    };

    return {
        chart: {
            height: 300,
        },
        xAxis: {
            labels: {
                rotation: expectedTickCount > 8 ? -45 : 0,
                formatter: function () {
                    return xAxisFormatter({ value: Number(this.value) }, isMonthly);
                },
            },
            startOnTick: true,
            showFirstLabel: true,
            tickPositioner: getXAxisTickPositioner,
        },
        yAxis: {
            max: displayMode === DISPLAY_MODES.percentage ? 100 : undefined,
            labels: {
                formatter: function () {
                    const value = Number(this.value);

                    if (displayMode === DISPLAY_MODES.percentage) {
                        return percentageSignOnlyFilter()(value, 0);
                    }

                    if (selectedMetric === EDataType.SPEND) {
                        return spendFilter(value, abbrNumberFilter());
                    }
                    return abbrNumberFilter()(value) as string;
                },
            },
        },
        tooltip: {
            positioner: tooltipPositioner,
            formatter() {
                if (!this.points?.length) {
                    return "";
                }

                const currentPointIndex = this.points[0].series.data.findIndex((point) => point.x === this.x);

                const allDates = new Set(
                    Object.values(selectedBreakdownData).flatMap((channelData) => channelData.map(([date]) => date)),
                );
                const sortedDates = Array.from(allDates).sort();
                const currentDate = sortedDates[currentPointIndex];

                const channelDataMap = (
                    Object.entries(selectedBreakdownData) as Array<
                        [ChannelKey, Array<[string, ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint]>]
                    >
                ).reduce(
                    (acc, [channel, data]) => {
                        const dataPoint = data.find(([date]) => date === currentDate)?.[1];
                        if (dataPoint) {
                            acc[channel] = dataPoint;
                        }
                        return acc;
                    },
                    {} as Record<ChannelKey, ProWebsiteControllersApiWidgetAdIntelligencePerformanceDataPoint>,
                );

                const data = this.points
                    .map((point) => {
                        const channelId = point.series.userOptions.id;
                        const channelData = channelDataMap[channelId];

                        if (!channelData) {
                            return null;
                        }

                        return {
                            label: {
                                value: point.series.name,
                                color: point.color,
                            },
                            count: {
                                value: point.y ? formatValue(point.y) : NA_VALUE,
                            },
                        };
                    })
                    .filter(Boolean);

                const tooltipTitleElements = getTooltipHeaderElement(granularityValue, this.x, to.toDate());

                return renderToString(
                    <ChartTooltip
                        withoutColumnTitles
                        width={280}
                        title={<TooltipTitle>{tooltipTitleElements}</TooltipTitle>}
                        columnDefinition={tooltipColumnDefinition}
                        // @ts-expect-error - item in data cannot be null
                        data={data}
                    />,
                );
            },
        },
        plotOptions: {
            area: {
                stacking: "normal" as const,
                marker: {
                    enabled: hasSingleDataPoint,
                },
            },
        },
    };
};
